import type { MonsterDef } from '../../sim/registry';
import type { StatusApply } from '../../sim/skillTypes';
import { monsterStats } from '../balance';
import { circle, cone, invuln, rect, teleport } from '../classes/kit';
import { areaStep, mAreas, mCombo, mLunge, mMelee, mShoot, mSkill, mSpin, mVolley } from './kit';

/**
 * Sunscar Expanse (field, levels 12–21): scorpions, raiders, risen legionnaires,
 * sand golems and the field boss Dunemaw. `sunscar_skeleton` and `sand_golem`
 * also guard the Sandsworn Tomb. Dunemaw's script: src/content/bosses/story.ts.
 */

const SAND = 0xd8b070;
const VENOM = 0x7fd13a;
const poison = (duration: number, value: number): StatusApply => ({ id: 'poison', duration, value });

/** Dunemaw digs in (invulnerable), then surfaces under the aim point with a launch. */
const burrow = (id: string, name: string, cd: number, text?: string) =>
  mSkill(id, name, {
    cd, range: [0, 30], maxRange: 30, superArmor: 'full', anim: 'Attack2', duration: 2.3, ...(text ? { announce: text, announceStyle: 'warning' as const } : {}),
    actions: [
      invuln(0.3, 1.35),
      teleport(1.55, 30, true),
      areaStep({ shape: circle(4.5), anchor: 'point', delay: 1.7, coef: 3, reaction: { kind: 'launch', power: 1, duration: 1 } }),
    ],
  });

export const SUNSCAR_MONSTERS: MonsterDef[] = [
  {
    id: 'sand_scorpion', name: 'Sand Scorpion', grade: 'normal', model: 'mob_scorpion', scale: 1, radius: 0.7, creature: 'Beast',
    stats: (l) => monsterStats(l, 'normal', { speed: 1.05 }),
    skills: [
      mMelee('scorp_pincer', 'Pincer Snap', { shape: cone(2.4, 80), coef: 1.1, windup: 0.4, cd: 2 }),
      mMelee('scorp_sting', 'Venom Sting', { shape: rect(3.4, 1.2), coef: 1.3, windup: 0.6, cd: 6, range: [0, 3.2], status: [poison(5, 0.12)] }),
      mLunge('scorp_skitter', 'Skittering Rush', { distance: 5, coef: 1.4, windup: 0.5, cd: 8, range: [2.5, 6] }),
    ],
    brain: 'melee', aggroRange: 9, leash: 22, xp: 42, loot: 'vale_beast', weakness: 'water',
  },
  {
    id: 'dune_raider', name: 'Dune Raider', grade: 'normal', model: 'mob_raider', scale: 1, radius: 0.45, creature: 'Human',
    stats: (l) => monsterStats(l, 'normal'),
    skills: [
      mMelee('raider_cut', 'Quick Cut', { shape: cone(2.2, 70), coef: 1, windup: 0.35, cd: 1.8 }),
      mCombo('raider_dance', 'Scimitar Dance', {
        hits: [
          { at: 0.4, shape: cone(2.4, 90), coef: 0.7 },
          { at: 0.72, shape: cone(2.6, 100), coef: 0.9, step: 0.5, reaction: { kind: 'knockback', power: 1.2 } },
        ],
        cd: 5,
      }),
      mMelee('raider_sand_toss', 'Sand Toss', { shape: cone(4, 50), coef: 0.5, windup: 0.5, cd: 9, range: [0, 3.8], status: [{ id: 'slow', duration: 3, value: -0.35 }] }),
    ],
    brain: 'melee', aggroRange: 9, leash: 22, xp: 45, loot: 'bandit',
  },
  {
    id: 'raider_slinger', name: 'Raider Slinger', grade: 'normal', model: 'mob_raider', scale: 0.95, radius: 0.45, tint: 0x8a6a4a, creature: 'Human',
    stats: (l) => monsterStats(l, 'normal', { hp: 0.8 }),
    skills: [
      mShoot('slinger_stone', 'Sling Stone', { coef: 1.1, speed: 20, range: 15, windup: 0.55, cd: 2.2 }),
      // Lobbed: lands where the target stood when the pot left the sling.
      mAreas('slinger_firepot', 'Fire Pot', {
        steps: [{ shape: circle(2.4), anchor: 'point', delay: 1, coef: 1.3, reaction: { kind: 'flinch' }, status: [{ id: 'burn', duration: 3, value: 0.12 }], element: 'fire' }],
        cd: 8, range: [0, 13], maxRange: 13, anim: 'Attack', hue: 0xff8a3a,
      }),
    ],
    brain: 'ranged', aggroRange: 13, leash: 24, xp: 45, loot: 'bandit',
  },
  {
    id: 'sunscar_skeleton', name: 'Sunscar Skeleton', grade: 'normal', model: 'mob_skeleton', scale: 1.05, radius: 0.5, tint: 0xd8b884, creature: 'Undead',
    stats: (l) => monsterStats(l, 'normal', { hp: 1.15, speed: 0.95 }),
    skills: [
      mMelee('sskel_khopesh', 'Khopesh Slash', { shape: cone(2.5, 90), coef: 1.2, windup: 0.45, cd: 2.2 }),
      mMelee('sskel_bash', 'Shield Bash', { shape: rect(2.4, 1.6), coef: 1, windup: 0.5, cd: 6, range: [0, 2.3], reaction: { kind: 'knockback', power: 2.2 } }),
      mSpin('sskel_whirl', 'Bone Whirl', { radius: 2.8, count: 3, interval: 0.22, coef: 1.5, windup: 0.55, cd: 9 }),
    ],
    brain: 'melee', aggroRange: 9, leash: 22, xp: 46, loot: 'normal', weakness: 'holy',
  },
  {
    id: 'sand_golem', name: 'Sand Golem', grade: 'elite', model: 'mob_golem', scale: 1, radius: 1, tint: 0xc8a86a, creature: 'Construct',
    stats: (l) => monsterStats(l, 'elite', { hp: 1.2, speed: 0.85 }),
    skills: [
      mMelee('golem_fist', 'Sandstone Fist', { shape: cone(3.4, 90), coef: 1.6, windup: 0.65, cd: 2.8, range: [0, 3], reaction: { kind: 'knockback', power: 2 } }),
      mAreas('golem_tremor', 'Tremor', { steps: [{ shape: circle(5), delay: 1, coef: 2.2 }], cd: 10, range: [0, 4], superArmor: 'paralysis', hue: SAND }),
      mVolley('golem_boulder', 'Boulder Hurl', { coef: 2, speed: 14, reach: 16, radius: 0.9, windup: 0.9, explode: circle(2.5), cd: 8, range: [4, 15], superArmor: 'paralysis', anim: 'Attack2' }),
      // Spikes march outward from the golem.
      mAreas('golem_spikes', 'Sand Spikes', {
        steps: [0, 1, 2].map((i) => ({ shape: rect(3.6, 2.4, 1 + i * 3.8), delay: 0.8 + i * 0.22, coef: 1.8, reaction: { kind: 'launch' as const, power: 0.4, duration: 0.8 } })),
        cd: 12, range: [0, 11], superArmor: 'paralysis', hue: SAND,
      }),
    ],
    brain: 'melee', aggroRange: 11, leash: 24, xp: 150, loot: 'elite', weakness: 'water',
  },
  {
    id: 'dunemaw', name: 'Dunemaw', grade: 'named', model: 'mob_scorpion', scale: 2.4, radius: 1.8, tint: 0xb07840, creature: 'Beast',
    title: 'Field Boss', stagger: 520, hpBars: 16, weakness: 'water',
    stats: (l) => monsterStats(l, 'named', { hp: 3.2, speed: 1.05 }),
    skills: [
      mCombo('dunemaw_pincers', 'Crushing Pincers', {
        hits: [
          { at: 0.55, shape: cone(4.2, 90), coef: 1.3 },
          { at: 0.95, shape: cone(4.5, 100), coef: 1.7, reaction: { kind: 'knockback', power: 2.5 } },
        ],
        cd: 4, range: [0, 4], superArmor: 'push',
      }),
      // Rear sweep: punishes players camping its back.
      mAreas('dunemaw_tail_sweep', 'Tail Sweep', {
        steps: [{ shape: cone(6.5, 220, 180), delay: 0.8, coef: 2.4, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3 } }],
        cd: 7, range: [0, 6], superArmor: 'push', anim: 'Attack2',
      }),
      mAreas('dunemaw_stinger', 'Stinger Lance', {
        steps: [{ shape: rect(7.5, 2), delay: 0.7, coef: 2.2, status: [poison(6, 0.15)], element: 'earth' }],
        cd: 6, range: [0, 7], superArmor: 'push', anim: 'Attack', hue: VENOM,
      }),
      // Burrow-and-emerge: digs in (invulnerable), surfaces under the aim point.
      burrow('dunemaw_burrow', 'Burrow Ambush', 16),
      // Mechanic (30%): the script chains this into two more ambushes.
      burrow('dunemaw_frenzy_burrow', 'Frenzied Burrow', 40, 'Dunemaw burrows into a frenzy! Keep moving!'),
      // Venom pools: persistent poison fields along the line to the target.
      mAreas('dunemaw_venom_rain', 'Venom Spray', {
        steps: [-4, 0, 4].map((off) => ({ shape: circle(2.6, off), anchor: 'point' as const, delay: 1, coef: 0.35, duration: 6, tick: 1, reaction: { kind: 'none' as const }, status: [poison(4, 0.15)], element: 'earth' as const })),
        cd: 12, range: [3, 16], maxRange: 16, anim: 'Attack', hue: VENOM,
      }),
      // Mechanic (60%): poison floods the cardinal lanes; the diagonals stay safe.
      mAreas('dunemaw_venom_tide', 'Venom Tide', {
        announce: 'Dunemaw floods the sands with venom! Find the diagonals!',
        steps: [0, 90, 180, 270].map((rot) => ({ shape: rect(20, 4.5, 0, rot), delay: 1.4, coef: 1.2, duration: 8, tick: 1, reaction: { kind: 'none' as const }, status: [poison(4, 0.2)], element: 'earth' as const })),
        cd: 40, range: [0, 99], superArmor: 'full', anim: 'Roar', hue: VENOM,
      }),
    ],
    brain: 'boss', aggroRange: 14, leash: 40, xp: 900, loot: 'field_boss',
  },
];
